import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Link as LinkIcon, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export function ImageUpload({ onImageSelected, currentImage, folderPath }) {
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image size must be less than 50MB');
      return;
    }

    setIsUploading(true);

    try {
      // Generate a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

      // Get signed upload URL from edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke('generate-upload-url', {
        body: {
          action: 'upload',
          fileName: fileName,
          contentType: file.type,
          folderPath: folderPath,
        },
      });

      if (urlError) throw urlError;

      // Upload file to GCS using signed URL
      const uploadResponse = await fetch(urlData.signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Set the preview and notify parent
      setPreview(urlData.publicUrl);
      onImageSelected(urlData.publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!imageUrl) {
      toast.error('Please enter an image URL');
      return;
    }

    const trimmed = imageUrl.trim();
    const isVariable = /\{\{.*?\}\}/.test(trimmed);

    if (!isVariable) {
      try {
        new URL(trimmed);
      } catch {
        toast.error('Please enter a valid URL or {{ variable }}');
        return;
      }
    }

    setPreview(isVariable ? null : trimmed);
    onImageSelected(trimmed);
    toast.success(isVariable ? 'Variable URL added' : 'Image URL added');
  };

  return (
    <div className="space-y-4">
      <Tabs value={uploadMethod} onValueChange={setUploadMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="url">Image URL</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Upload Image</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose an image file from your computer
            </p>
            <label htmlFor="image-upload">
              <Button asChild disabled={isUploading}>
                <span>
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Choose File'
                  )}
                </span>
              </Button>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Supported formats: JPG, PNG, GIF, WebP (Max 50MB)
          </div>
        </TabsContent>

        {/* URL Tab */}
        <TabsContent value="url" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="image-url">Image URL</Label>
            <div className="flex gap-2">
              <Input
                id="image-url"
                placeholder="https://example.com/image.jpg or {{ variable }}"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUrlSubmit();
                  }
                }}
              />
              <Button onClick={handleUrlSubmit}>
                <LinkIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview */}
      {preview && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="border rounded-lg overflow-hidden">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-64 object-contain"
              onError={() => {
                toast.error('Failed to load image preview');
                setPreview(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
